import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import Map, {
  Layer,
  Marker,
  NavigationControl,
  Popup,
  Source,
  type MapRef,
} from 'react-map-gl/maplibre';
import type { StyleSpecification } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { circlePolygon } from '@/lib/geo';
import {
  AlertCircle,
  Anchor,
  Check,
  ChevronDown,
  ChevronUp,
  Eye,
  Info,
  Layers,
  SlidersHorizontal,
  ListFilter,
  Locate,
  Lock,
  Loader2,
  MapIcon as MapExternal,
  MapPin,
  Navigation,
  PanelLeftClose,
  PanelLeftOpen,
  RefreshCw,
  RotateCcw,
  Search,
  Terminal,
  Unlock,
  X,
} from 'lucide-react';
import { commandsApi, GT06_COMMANDS } from '@/api/endpoints/commands';
import { geofencesApi } from '@/api/endpoints/geofences';
import { rentalsApi } from '@/api/endpoints/rentals';
import { useCommandsStore } from '@/stores/commands';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '@/api/endpoints/reports';
import { marineApi } from '@/api/endpoints/marine';
import { areaToGeoJSON, parseWkt } from '@/lib/wkt';
import { extractUF, formatAddress } from '@/lib/address';
import { Input } from '@/components/ui/input';
import { useFleetStore } from '@/stores/fleet';
import { useEventsStore } from '@/stores/events';
import { StatusBadge } from '@/components/device/StatusBadge';
import { DevicePhoto } from '@/components/device/DevicePhoto';
import {
  computeMachineStatus,
  computeOperationalStatus,
  getDeviceNickname,
  isBlocked,
  readTelemetry,
  STATUS_ORDER,
  type MachineStatus,
} from '@/lib/machineStatus';
import { TelemetryIcons } from '@/components/device/TelemetryIcons';
import { VesselIcon } from '@/components/device/vesselIcons';
import { getDeviceIconColor } from '@/lib/vesselCategories';
import {
  cn,
  formatCoords,
  formatDateTime,
  formatRelativeTime,
  formatSpeed,
} from '@/lib/utils';
import type { Device, Position } from '@/api/types';
import { mensagemDeErro } from '@/lib/apiError';

const STREETS_STYLE: StyleSpecification = {
  version: 8,
  sources: {
    'carto-voyager': {
      type: 'raster',
      tiles: [
        'https://a.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png',
        'https://b.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png',
        'https://c.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png',
        'https://d.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}@2x.png',
      ],
      tileSize: 256,
      attribution:
        '© <a href="https://carto.com/attributions">CARTO</a> · © <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    },
  },
  layers: [{ id: 'carto-voyager', type: 'raster', source: 'carto-voyager' }],
};

const SATELLITE_STYLE: StyleSpecification = {
  version: 8,
  sources: {
    esri: {
      type: 'raster',
      tiles: [
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      ],
      tileSize: 256,
      attribution: 'Tiles © Esri',
    },
    labels: {
      type: 'raster',
      tiles: [
        'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
      ],
      tileSize: 256,
    },
  },
  layers: [
    { id: 'esri', type: 'raster', source: 'esri' },
    { id: 'labels', type: 'raster', source: 'labels' },
  ],
};

const STATUS_HEX: Record<MachineStatus, string> = {
  running: '#34C759',
  ready: '#FF9500',
  blocked: '#FF3B30',
  off: '#8E8E93',
};

/** Modo de segurança (attributes.modo) → rótulo curto (badge no mapa/lista). */
const MODO_LABEL: Record<string, string> = {
  seguranca: 'Segurança',
  manutencao: 'Manutenção',
  transporte: 'Transporte',
  locacao: 'Locação',
};
function deviceModo(device: Device): string | null {
  const m = device.attributes?.modo;
  return typeof m === 'string' && MODO_LABEL[m] ? MODO_LABEL[m] : null;
}

const SHORT_LABEL: Record<MachineStatus, string> = {
  running: 'Naveg.',
  ready: 'Atrac.',
  blocked: 'Bloq.',
  off: 'Offline',
};

interface FleetItem {
  device: Device;
  position: Position;
  status: MachineStatus;
  uf: string | null;
}

type StatusFilter = 'all' | MachineStatus;
type UfFilter = 'all' | 'unknown' | string;
type FlagFilter = 'none' | 'lowbattery' | 'alert' | 'maintenance' | 'rental';
type BasemapKind = 'streets' | 'satellite';

const LOW_BATTERY_PCT = 20;

export function MapPage() {
  const devices = useFleetStore((s) => s.devices);
  const positions = useFleetStore((s) => s.positions);
  const mapRef = useRef<MapRef | null>(null);

  const [searchParams] = useSearchParams();
  const queryDevice = searchParams.get('device');
  const focusedFromQueryRef = useRef<string | null>(null);

  const [selectedId, setSelectedId] = useState<number | null>(
    queryDevice ? Number(queryDevice) : null,
  );
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [flagFilter, setFlagFilter] = useState<FlagFilter>('none');
  const [ufFilter, setUfFilter] = useState<UfFilter>('all');

  // Embarcações com alerta = têm evento não-lido no feed.
  const eventItems = useEventsStore((s) => s.items);
  const alertDeviceIds = useMemo(() => {
    const set = new Set<number>();
    for (const e of eventItems) if (!e.read && e.deviceId != null) set.add(e.deviceId);
    return set;
  }, [eventItems]);
  const [panelOpenMobile, setPanelOpenMobile] = useState(false);
  const [basemap, setBasemap] = useState<BasemapKind>('streets');
  const [seamark, setSeamark] = useState(true);
  const [panelHidden, setPanelHidden] = useState(false);
  const [commandDevice, setCommandDevice] = useState<Device | null>(null);

  const items = useMemo<FleetItem[]>(() => {
    const list: FleetItem[] = [];
    for (const device of devices.values()) {
      const position = positions.get(device.id);
      if (
        !position ||
        position.latitude == null ||
        position.longitude == null ||
        Number.isNaN(position.latitude) ||
        Number.isNaN(position.longitude)
      ) {
        continue;
      }
      list.push({
        device,
        position,
        status: computeMachineStatus(device, position),
        uf: extractUF(position.address),
      });
    }
    return list;
  }, [devices, positions]);

  const counts = useMemo(() => {
    const c: Record<MachineStatus, number> = {
      running: 0,
      ready: 0,
      blocked: 0,
      off: 0,
    };
    for (const it of items) c[it.status] += 1;
    return c;
  }, [items]);

  const ufOptions = useMemo(() => {
    const counter: Record<string, number> = {};
    let unknownCount = 0;
    for (const it of items) {
      if (it.uf) counter[it.uf] = (counter[it.uf] ?? 0) + 1;
      else unknownCount += 1;
    }
    const list = Object.entries(counter).sort((a, b) =>
      a[0].localeCompare(b[0]),
    ) as [string, number][];
    return { list, unknownCount };
  }, [items]);

  const isLowBattery = (it: FleetItem) => {
    const lvl = readTelemetry(it.position).batteryLevel;
    return lvl != null && lvl <= LOW_BATTERY_PCT;
  };

  const rentalsQuery = useQuery({ queryKey: ['rentals'], queryFn: () => rentalsApi.list(), staleTime: 300_000 });
  const activeRentalDeviceIds = useMemo(() => {
    const s = new Set<number>();
    for (const r of rentalsQuery.data ?? []) if (!r.finalizada) s.add(r.device_id);
    return s;
  }, [rentalsQuery.data]);
  const isMaintenance = (it: FleetItem) => deviceModo(it.device) === 'Manutenção';
  const isRental = (it: FleetItem) => activeRentalDeviceIds.has(it.device.id);

  const flagCounts = useMemo(
    () => ({
      lowbattery: items.filter(isLowBattery).length,
      alert: items.filter((it) => alertDeviceIds.has(it.device.id)).length,
      maintenance: items.filter(isMaintenance).length,
      rental: items.filter(isRental).length,
    }),
    [items, alertDeviceIds, activeRentalDeviceIds],
  );

  const visibleItems = useMemo(() => {
    let list = items;
    if (statusFilter !== 'all') list = list.filter((it) => it.status === statusFilter);
    if (flagFilter === 'lowbattery') list = list.filter(isLowBattery);
    else if (flagFilter === 'alert') list = list.filter((it) => alertDeviceIds.has(it.device.id));
    else if (flagFilter === 'maintenance') list = list.filter(isMaintenance);
    else if (flagFilter === 'rental') list = list.filter(isRental);
    if (ufFilter === 'unknown') list = list.filter((it) => !it.uf);
    else if (ufFilter !== 'all') list = list.filter((it) => it.uf === ufFilter);
    return list;
  }, [items, statusFilter, flagFilter, ufFilter, alertDeviceIds, activeRentalDeviceIds]);

  const filteredListItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = visibleItems;
    if (q) {
      list = list.filter(
        (it) =>
          it.device.name.toLowerCase().includes(q) ||
          it.device.uniqueId.toLowerCase().includes(q) ||
          (getDeviceNickname(it.device)?.toLowerCase().includes(q) ?? false),
      );
    }
    return [...list].sort((a, b) => {
      const ta = new Date(a.position.fixTime).getTime();
      const tb = new Date(b.position.fixTime).getTime();
      return tb - ta;
    });
  }, [visibleItems, search]);

  const initialView = useMemo(() => {
    if (items.length === 0) return { longitude: -47, latitude: -15, zoom: 3.5 };
    let lonSum = 0;
    let latSum = 0;
    for (const it of items) {
      lonSum += it.position.longitude!;
      latSum += it.position.latitude!;
    }
    return {
      longitude: lonSum / items.length,
      latitude: latSum / items.length,
      zoom: 4,
    };
  }, [items]);

  const selected = useMemo(
    () => (selectedId != null ? items.find((it) => it.device.id === selectedId) : undefined),
    [items, selectedId],
  );

  const accuracyCircle = useMemo(() => {
    if (!selected) return null;
    return circlePolygon(selected.position.longitude!, selected.position.latitude!, 50);
  }, [selected]);

  // Trajeto da embarcação selecionada (additivo): janela de horas; 0 = desligado.
  const [trackHours, setTrackHours] = useState(0);
  const trackQuery = useQuery({
    queryKey: ['track', selectedId, trackHours],
    enabled: selectedId != null && trackHours > 0,
    queryFn: () =>
      reportsApi.route({
        deviceId: selectedId!,
        from: new Date(Date.now() - trackHours * 3_600_000).toISOString(),
        to: new Date().toISOString(),
      }),
    staleTime: 60_000,
  });
  const trackGeoJson = useMemo(() => {
    const empty = { type: 'FeatureCollection' as const, features: [] };
    if (selectedId == null || trackHours === 0) return empty;
    const pts = (trackQuery.data ?? [])
      .filter((p) => p.longitude != null && p.latitude != null)
      .map((p) => [p.longitude, p.latitude] as [number, number]);
    return pts.length > 1
      ? { type: 'FeatureCollection' as const, features: [{ type: 'Feature' as const, properties: {}, geometry: { type: 'LineString' as const, coordinates: pts } }] }
      : empty;
  }, [trackQuery.data, selectedId, trackHours]);

  // Camada de referências náuticas (base PostGIS) por bbox/zoom.
  const [showRefs, setShowRefs] = useState(false);
  const [bounds, setBounds] = useState<[number, number, number, number] | null>(null);
  const [zoom, setZoom] = useState(0);
  const captureView = (m: { getBounds: () => { getWest(): number; getSouth(): number; getEast(): number; getNorth(): number }; getZoom: () => number }) => {
    const b = m.getBounds();
    setBounds([+b.getWest().toFixed(2), +b.getSouth().toFixed(2), +b.getEast().toFixed(2), +b.getNorth().toFixed(2)]);
    setZoom(Math.round(m.getZoom()));
  };
  const refsQuery = useQuery({
    queryKey: ['marine-layer', bounds, showRefs && zoom >= 9],
    enabled: showRefs && zoom >= 9 && bounds != null,
    queryFn: () => marineApi.layer({ minlon: bounds![0], minlat: bounds![1], maxlon: bounds![2], maxlat: bounds![3] }),
    staleTime: 120_000,
  });

  // Cluster por grade (sem dependência): só agrupa com muitas embarcações em zoom baixo;
  // caso contrário, comportamento atual (markers individuais). NB: `Map` aqui é o componente
  // react-map-gl, então usamos um objeto como dicionário de buckets.
  const clustered = useMemo(() => {
    if (visibleItems.length <= 25 || zoom === 0 || zoom >= 14) {
      return { clusters: [] as { lon: number; lat: number; count: number }[], points: visibleItems };
    }
    const cell = 360 / Math.pow(2, zoom + 1);
    const buckets: Record<string, { items: FleetItem[]; lonSum: number; latSum: number }> = {};
    for (const it of visibleItems) {
      const lon = it.position.longitude!, lat = it.position.latitude!;
      const key = `${Math.floor(lon / cell)}:${Math.floor(lat / cell)}`;
      const b = buckets[key] ?? { items: [], lonSum: 0, latSum: 0 };
      b.items.push(it);
      b.lonSum += lon;
      b.latSum += lat;
      buckets[key] = b;
    }
    const clusters: { lon: number; lat: number; count: number }[] = [];
    const points: FleetItem[] = [];
    for (const b of Object.values(buckets)) {
      if (b.items.length === 1) points.push(b.items[0]);
      else clusters.push({ lon: b.lonSum / b.items.length, lat: b.latSum / b.items.length, count: b.items.length });
    }
    return { clusters, points };
  }, [visibleItems, zoom]);

  const geofencesQuery = useQuery({
    queryKey: ['geofences'],
    queryFn: () => geofencesApi.list(),
    staleTime: 60_000,
  });

  const geofencesGeoJson = useMemo(() => {
    const features = (geofencesQuery.data ?? [])
      .map((g) => {
        const area = parseWkt(g.area);
        if (!area) return null;
        const feature = areaToGeoJSON(area);
        const a = g.attributes ?? {};
        const color =
          typeof a.color === 'string' && (a.color as string).trim()
            ? (a.color as string)
            : '#0A84FF';
        feature.properties = {
          id: g.id,
          name: g.name,
          color,
          isLine: area.type === 'LINESTRING',
        };
        return feature;
      })
      .filter((f): f is GeoJSON.Feature<GeoJSON.Geometry> => f !== null);
    return {
      type: 'FeatureCollection' as const,
      features,
    };
  }, [geofencesQuery.data]);

  const focusOn = (item: FleetItem) => {
    setSelectedId(item.device.id);
    mapRef.current?.flyTo({
      center: [item.position.longitude!, item.position.latitude!],
      zoom: 14,
      duration: 800,
    });
    setPanelOpenMobile(false);
  };

  // Quando a página é aberta com ?device=<id>, dá flyTo na embarcação assim que
  // os dados estiverem disponíveis. Usa ref pra disparar uma única vez.
  useEffect(() => {
    if (!queryDevice) return;
    if (focusedFromQueryRef.current === queryDevice) return;
    const id = Number(queryDevice);
    const item = items.find((it) => it.device.id === id);
    if (item) {
      focusOn(item);
      focusedFromQueryRef.current = queryDevice;
    }
  }, [queryDevice, items]);

  const recenterAll = () => {
    if (visibleItems.length === 0) return;
    if (visibleItems.length === 1) {
      const it = visibleItems[0];
      mapRef.current?.flyTo({
        center: [it.position.longitude!, it.position.latitude!],
        zoom: 14,
        duration: 600,
      });
      return;
    }
    let minLng = Infinity;
    let minLat = Infinity;
    let maxLng = -Infinity;
    let maxLat = -Infinity;
    for (const it of visibleItems) {
      minLng = Math.min(minLng, it.position.longitude!);
      minLat = Math.min(minLat, it.position.latitude!);
      maxLng = Math.max(maxLng, it.position.longitude!);
      maxLat = Math.max(maxLat, it.position.latitude!);
    }
    mapRef.current?.fitBounds(
      [
        [minLng, minLat],
        [maxLng, maxLat],
      ],
      { padding: 80, duration: 600, maxZoom: 14 },
    );
  };

  return (
    <div className="relative flex h-full">
      {!panelHidden && (
        <DeviceListPanel
          items={filteredListItems}
          total={items.length}
          counts={counts}
          ufOptions={ufOptions}
          ufFilter={ufFilter}
          onUfFilterChange={setUfFilter}
          search={search}
          onSearchChange={setSearch}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          flagFilter={flagFilter}
          onFlagChange={setFlagFilter}
          flagCounts={flagCounts}
          selectedId={selectedId}
          onSelect={focusOn}
          openMobile={panelOpenMobile}
          onCloseMobile={() => setPanelOpenMobile(false)}
          onHide={() => setPanelHidden(true)}
        />
      )}

      <div className="relative flex-1">
        {panelHidden && (
          <button
            type="button"
            onClick={() => setPanelHidden(false)}
            title="Mostrar painel de embarcações"
            className="absolute left-0 top-1/2 z-10 hidden -translate-y-1/2 items-center rounded-r-lg border border-l-0 border-border bg-card/95 py-4 pl-1 pr-1.5 text-foreground shadow backdrop-blur hover:bg-accent lg:flex"
          >
            <PanelLeftOpen className="h-4 w-4" />
          </button>
        )}
        {selected && (
          <div className="absolute left-3 top-3 z-10 flex items-center gap-1 rounded-full bg-card/95 p-1 shadow ring-1 ring-border">
            <span className="px-2 text-[11px] font-semibold text-muted-foreground">Trajeto</span>
            {([[0, 'Off'], [6, '6h'], [24, '24h'], [72, '3d']] as const).map(([h, lbl]) => (
              <button
                key={h}
                type="button"
                onClick={() => setTrackHours(h)}
                className={cn(
                  'rounded-full px-2 py-0.5 text-[11px] font-semibold',
                  trackHours === h ? 'bg-brand text-white' : 'text-muted-foreground hover:bg-accent',
                )}
              >
                {lbl}
              </button>
            ))}
            {trackQuery.isFetching && <span className="px-1 text-[10px] text-muted-foreground">…</span>}
          </div>
        )}
        <div className="absolute bottom-3 left-3 z-10 flex items-center gap-2 rounded-full bg-card/95 px-2 py-1 shadow ring-1 ring-border">
          <button
            type="button"
            onClick={() => setShowRefs((v) => !v)}
            className={cn(
              'rounded-full px-2 py-0.5 text-[11px] font-semibold',
              showRefs ? 'bg-brand text-white' : 'text-muted-foreground hover:bg-accent',
            )}
          >
            Base náutica
          </button>
          {showRefs && zoom < 9 && <span className="text-[10px] text-muted-foreground">aproxime para ver</span>}
          {showRefs && zoom >= 9 && refsQuery.isFetching && <span className="text-[10px] text-muted-foreground">…</span>}
          {showRefs && zoom >= 9 && refsQuery.data && (
            <span className="text-[10px] text-muted-foreground">{refsQuery.data.features.length} refs</span>
          )}
        </div>
        <Map
          ref={mapRef}
          initialViewState={initialView}
          mapStyle={basemap === 'streets' ? STREETS_STYLE : SATELLITE_STYLE}
          style={{ width: '100%', height: '100%' }}
          attributionControl={false}
          onLoad={(e) => captureView(e.target)}
          onMoveEnd={(e) => captureView(e.target)}
        >
          <NavigationControl position="top-right" showCompass={false} />

          {seamark && (
            <Source
              id="openseamap"
              type="raster"
              tiles={['https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png']}
              tileSize={256}
              attribution='© <a href="https://www.openseamap.org/">OpenSeaMap</a>'
            >
              <Layer id="openseamap-seamark" type="raster" paint={{ 'raster-opacity': 0.95 }} />
            </Source>
          )}

          {geofencesGeoJson.features.length > 0 && (
            <Source id="geofences" type="geojson" data={geofencesGeoJson}>
              <Layer
                id="geofences-fill"
                type="fill"
                filter={['!=', ['get', 'isLine'], true]}
                paint={{
                  'fill-color': ['get', 'color'],
                  'fill-opacity': 0.12,
                }}
              />
              <Layer
                id="geofences-line"
                type="line"
                paint={{
                  'line-color': ['get', 'color'],
                  'line-width': ['case', ['==', ['get', 'isLine'], true], 3, 2],
                  'line-opacity': 0.8,
                }}
              />
            </Source>
          )}

          {trackGeoJson.features.length > 0 && (
            <Source id="track" type="geojson" data={trackGeoJson}>
              <Layer id="track-line" type="line" paint={{ 'line-color': '#0B3A5B', 'line-width': 3, 'line-opacity': 0.85 }} />
            </Source>
          )}

          {showRefs && refsQuery.data && refsQuery.data.features.length > 0 && (
            <Source id="marine-refs" type="geojson" data={refsQuery.data}>
              <Layer
                id="marine-refs-circle"
                type="circle"
                paint={{
                  'circle-radius': ['interpolate', ['linear'], ['zoom'], 9, 3, 16, 6],
                  'circle-color': ['match', ['get', 'tipo'],
                    'marina', '#007AFF',
                    'porto_terminal', '#8b5cf6',
                    'farol', '#f59e0b',
                    'ilha', '#16a34a',
                    'praia', '#fbbf24',
                    'massa_agua', '#0891b2',
                    '#64748b'],
                  'circle-opacity': 0.85,
                  'circle-stroke-width': 1,
                  'circle-stroke-color': '#ffffff',
                }}
              />
            </Source>
          )}

          {accuracyCircle && (
            <Source id="accuracy-circle" type="geojson" data={accuracyCircle}>
              <Layer
                id="accuracy-circle-fill"
                type="fill"
                paint={{
                  'fill-color': '#0A84FF',
                  'fill-opacity': 0.18,
                }}
              />
              <Layer
                id="accuracy-circle-outline"
                type="line"
                paint={{
                  'line-color': '#0A84FF',
                  'line-width': 2,
                  'line-opacity': 0.7,
                }}
              />
            </Source>
          )}

          {clustered.clusters.map((c, i) => (
            <Marker
              key={`cluster-${i}`}
              longitude={c.lon}
              latitude={c.lat}
              anchor="center"
              onClick={(e) => {
                e.originalEvent.stopPropagation();
                mapRef.current?.flyTo({ center: [c.lon, c.lat], zoom: Math.min((zoom || 10) + 2, 15) });
              }}
            >
              <button
                type="button"
                title={`${c.count} embarcações — toque para aproximar`}
                className="flex items-center justify-center rounded-full bg-brand font-bold text-white shadow-md ring-2 ring-white transition-transform hover:scale-110"
                style={{ height: 38, width: 38, fontSize: c.count > 99 ? 12 : 14 }}
              >
                {c.count}
              </button>
            </Marker>
          ))}

          {clustered.points.map((it) => (
            <Marker
              key={it.device.id}
              longitude={it.position.longitude!}
              latitude={it.position.latitude!}
              anchor="bottom"
              onClick={(e) => {
                e.originalEvent.stopPropagation();
                focusOn(it);
              }}
            >
              {(() => {
                const selected = selectedId === it.device.id;
                const statusColor = STATUS_HEX[it.status];
                // Corpo na cor escolhida da embarcação; status fica no pino inferior.
                const bodyColor = getDeviceIconColor(it.device.attributes) ?? statusColor;
                return (
                  <button
                    type="button"
                    className={cn(
                      'flex flex-col items-center transition-transform hover:scale-110',
                      selected && 'scale-110',
                      it.status === 'off' && 'opacity-70',
                    )}
                    title={it.device.name}
                  >
                    <span
                      className={cn(
                        'flex items-center justify-center rounded-full text-white shadow-md ring-2 ring-white',
                        selected ? 'h-10 w-10' : 'h-8 w-8',
                      )}
                      style={{ background: bodyColor }}
                    >
                      <VesselIcon category={it.device.category} className={selected ? 'h-6 w-6' : 'h-5 w-5'} />
                    </span>
                    <span
                      className="-mt-1 h-2.5 w-2.5 rotate-45 rounded-[2px] ring-2 ring-white"
                      style={{ background: statusColor }}
                    />
                  </button>
                );
              })()}
            </Marker>
          ))}

          {selected && (
            <Popup
              longitude={selected.position.longitude!}
              latitude={selected.position.latitude!}
              anchor="bottom"
              offset={36}
              closeButton
              closeOnClick={false}
              onClose={() => setSelectedId(null)}
              maxWidth="380px"
            >
              <PopupContent
                {...selected}
                onOpenCommands={() => setCommandDevice(selected.device)}
              />
            </Popup>
          )}
        </Map>

        {/* Marca d'água NautMax (canto inferior esquerdo, não interativa) */}
        <img
          src={`${import.meta.env.BASE_URL}brand/nautmax-logo.png`}
          alt="NautMax"
          className="pointer-events-none absolute bottom-2 left-2 z-10 h-7 w-auto opacity-70 drop-shadow-sm sm:h-8"
        />

        {commandDevice && (
          <CommandModal device={commandDevice} onClose={() => setCommandDevice(null)} />
        )}

        <FilterBar
          counts={counts}
          total={items.length}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
        />

        <button
          type="button"
          onClick={() => setPanelOpenMobile(true)}
          className="absolute left-3 top-16 z-10 inline-flex items-center gap-1.5 rounded-full bg-card px-3 py-2 text-xs font-semibold shadow ring-1 ring-border lg:hidden"
        >
          <ListFilter className="h-4 w-4" />
          Lista · {filteredListItems.length}
        </button>

        <FabStack
          basemap={basemap}
          onToggleBasemap={() => setBasemap((b) => (b === 'streets' ? 'satellite' : 'streets'))}
          seamark={seamark}
          onToggleSeamark={() => setSeamark((v) => !v)}
          onRecenter={recenterAll}
          onTogglePanel={() => setPanelHidden((v) => !v)}
          panelHidden={panelHidden}
        />
      </div>
    </div>
  );
}

function FilterBar({
  counts,
  total,
  statusFilter,
  onStatusFilterChange,
}: {
  counts: Record<MachineStatus, number>;
  total: number;
  statusFilter: StatusFilter;
  onStatusFilterChange: (s: StatusFilter) => void;
}) {
  return (
    <div className="absolute left-1/2 top-3 z-10 flex max-w-[calc(100%-1rem)] -translate-x-1/2 flex-wrap items-center justify-center gap-1 rounded-full bg-card/95 px-2 py-1 shadow ring-1 ring-border backdrop-blur">
      <FilterChip
        label={`Todos · ${total}`}
        active={statusFilter === 'all'}
        onClick={() => onStatusFilterChange('all')}
      />
      {STATUS_ORDER.map((s) => (
        <FilterChip
          key={s}
          label={`${SHORT_LABEL[s]} ${counts[s]}`}
          color={STATUS_HEX[s]}
          active={statusFilter === s}
          onClick={() => onStatusFilterChange(s)}
        />
      ))}
    </div>
  );
}

function FabStack({
  basemap,
  onToggleBasemap,
  seamark,
  onToggleSeamark,
  onRecenter,
  onTogglePanel,
  panelHidden,
}: {
  basemap: BasemapKind;
  onToggleBasemap: () => void;
  seamark: boolean;
  onToggleSeamark: () => void;
  onRecenter: () => void;
  onTogglePanel: () => void;
  panelHidden: boolean;
}) {
  return (
    <div className="absolute right-3 top-32 z-10 flex flex-col gap-2">
      <FabButton
        onClick={onToggleSeamark}
        title={seamark ? 'Ocultar sinalização náutica' : 'Mostrar sinalização náutica (OpenSeaMap)'}
        active={seamark}
      >
        <Anchor className="h-4 w-4" />
        <span className="hidden text-[11px] font-bold sm:inline">Náutico</span>
      </FabButton>
      <FabButton
        onClick={onToggleBasemap}
        title={basemap === 'streets' ? 'Mostrar satélite' : 'Mostrar mapa'}
        active={basemap === 'satellite'}
      >
        <Layers className="h-4 w-4" />
        <span className="hidden text-[11px] font-bold sm:inline">
          {basemap === 'streets' ? 'Satélite' : 'Mapa'}
        </span>
      </FabButton>
      <FabButton onClick={onRecenter} title="Centralizar na frota">
        <Locate className="h-4 w-4" />
      </FabButton>
      <FabButton
        onClick={onTogglePanel}
        title={panelHidden ? 'Mostrar lista' : 'Esconder lista'}
        className="hidden lg:inline-flex"
      >
        <ListFilter className="h-4 w-4" />
      </FabButton>
      <FabButton onClick={() => window.location.reload()} title="Recarregar">
        <RefreshCw className="h-4 w-4" />
      </FabButton>
    </div>
  );
}

function FabButton({
  children,
  onClick,
  title,
  active,
  className,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
  active?: boolean;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={cn(
        'inline-flex items-center justify-center gap-1.5 rounded-full bg-card px-3 py-2 shadow-lg ring-1 ring-border transition-colors hover:bg-accent',
        active && 'bg-primary text-primary-foreground hover:bg-primary/90',
        className,
      )}
    >
      {children}
    </button>
  );
}

function FilterChip({
  label,
  title,
  color,
  active,
  onClick,
  count,
  dim,
}: {
  label: string;
  title?: string;
  color?: string;
  active: boolean;
  onClick: () => void;
  count?: number;
  /** Esmaece (sem itens), mas continua clicável e com tooltip. */
  dim?: boolean;
}) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-[11px] font-semibold transition-all',
        active
          ? 'bg-secondary text-foreground ring-1 ring-border'
          : 'text-muted-foreground hover:bg-accent',
        dim && !active && 'opacity-45',
      )}
    >
      {color && (
        <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />
      )}
      <span>{label}</span>
      {count !== undefined && count > 0 && (
        <span className="inline-flex min-w-[1.1rem] items-center justify-center rounded-full bg-foreground/10 px-1 text-[10px] font-bold tabular-nums text-foreground">
          {count}
        </span>
      )}
    </button>
  );
}

function DeviceListPanel({
  items,
  total,
  counts,
  ufOptions,
  ufFilter,
  onUfFilterChange,
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  flagFilter,
  onFlagChange,
  flagCounts,
  selectedId,
  onSelect,
  openMobile,
  onCloseMobile,
  onHide,
}: {
  items: FleetItem[];
  total: number;
  counts: Record<MachineStatus, number>;
  ufOptions: { list: [string, number][]; unknownCount: number };
  ufFilter: UfFilter;
  onUfFilterChange: (v: UfFilter) => void;
  search: string;
  onSearchChange: (v: string) => void;
  statusFilter: StatusFilter;
  onStatusFilterChange: (s: StatusFilter) => void;
  flagFilter: FlagFilter;
  onFlagChange: (f: FlagFilter) => void;
  flagCounts: { lowbattery: number; alert: number; maintenance: number; rental: number };
  selectedId: number | null;
  onSelect: (it: FleetItem) => void;
  openMobile: boolean;
  onCloseMobile: () => void;
  onHide?: () => void;
}) {
  const [filtersOpen, setFiltersOpen] = useState(true);
  return (
    <>
      {openMobile && (
        <div
          className="fixed inset-0 z-30 bg-background/40 lg:hidden"
          onClick={onCloseMobile}
          aria-hidden
        />
      )}
      <aside
        className={cn(
          'flex w-[320px] shrink-0 flex-col border-r border-border bg-card',
          'fixed inset-y-0 left-0 z-40 transition-transform lg:static lg:translate-x-0',
          openMobile ? 'translate-x-0 shadow-xl' : '-translate-x-full lg:translate-x-0',
        )}
      >
        <header className="space-y-3 border-b border-border p-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold">
              Embarcações <span className="text-muted-foreground">· {items.length}/{total}</span>
            </h2>
            <div className="flex items-center gap-1">
              {onHide && (
                <button
                  type="button"
                  onClick={onHide}
                  className="hidden items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold text-muted-foreground hover:bg-accent hover:text-foreground lg:inline-flex"
                  title="Esconder painel"
                >
                  <PanelLeftClose className="h-4 w-4" />
                  Esconder
                </button>
              )}
              <button
                type="button"
                onClick={onCloseMobile}
                className="rounded-md p-1 text-muted-foreground hover:bg-accent lg:hidden"
                aria-label="Fechar"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Buscar"
              className="rounded-full bg-secondary pl-9"
            />
          </div>
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => setFiltersOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 text-[11px] font-semibold text-muted-foreground hover:bg-accent hover:text-foreground"
              title={filtersOpen ? 'Esconder filtros' : 'Mostrar filtros'}
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              Filtros
              {filtersOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </button>
          </div>
          {filtersOpen && (
            <>
              <div className="flex flex-wrap items-center gap-1.5">
                <FilterChip
                  label="Todos"
                  active={statusFilter === 'all'}
                  onClick={() => onStatusFilterChange('all')}
                />
                {STATUS_ORDER.map((s) => (
                  <FilterChip
                    key={s}
                    label={SHORT_LABEL[s]}
                    count={counts[s]}
                    color={STATUS_HEX[s]}
                    active={statusFilter === s}
                    onClick={() => onStatusFilterChange(s)}
                  />
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-1.5">
                <FilterChip
                  label="🔋"
                  title="Bateria baixa"
                  count={flagCounts.lowbattery}
                  dim={flagCounts.lowbattery === 0}
                  active={flagFilter === 'lowbattery'}
                  onClick={() => onFlagChange(flagFilter === 'lowbattery' ? 'none' : 'lowbattery')}
                />
                <FilterChip
                  label="🔔"
                  title="Com alerta"
                  count={flagCounts.alert}
                  dim={flagCounts.alert === 0}
                  active={flagFilter === 'alert'}
                  onClick={() => onFlagChange(flagFilter === 'alert' ? 'none' : 'alert')}
                />
                <FilterChip
                  label="🔧"
                  title="Em manutenção"
                  count={flagCounts.maintenance}
                  dim={flagCounts.maintenance === 0}
                  active={flagFilter === 'maintenance'}
                  onClick={() => onFlagChange(flagFilter === 'maintenance' ? 'none' : 'maintenance')}
                />
                <FilterChip
                  label="🔑"
                  title="Em locação"
                  count={flagCounts.rental}
                  dim={flagCounts.rental === 0}
                  active={flagFilter === 'rental'}
                  onClick={() => onFlagChange(flagFilter === 'rental' ? 'none' : 'rental')}
                />
              </div>
              <label
                className={cn(
                  'inline-flex w-fit items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] font-semibold',
                  ufFilter !== 'all'
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground',
                )}
              >
                <span>UF</span>
                <select
                  value={ufFilter}
                  onChange={(e) => onUfFilterChange(e.target.value)}
                  className="cursor-pointer bg-transparent outline-none"
                >
                  <option value="all">Todas · {total}</option>
                  {ufOptions.list.map(([uf, count]) => (
                    <option key={uf} value={uf}>
                      {uf} · {count}
                    </option>
                  ))}
                  {ufOptions.unknownCount > 0 && (
                    <option value="unknown">Indefinida · {ufOptions.unknownCount}</option>
                  )}
                </select>
              </label>
            </>
          )}
        </header>
        <ul className="flex-1 divide-y divide-border overflow-y-auto">
          {items.length === 0 ? (
            <li className="p-4 text-center text-xs text-muted-foreground">
              Nenhuma embarcação nesse filtro.
            </li>
          ) : (
            items.map((it) => (
              <DeviceItem
                key={it.device.id}
                item={it}
                active={selectedId === it.device.id}
                onClick={() => onSelect(it)}
              />
            ))
          )}
        </ul>
      </aside>
    </>
  );
}

function DeviceItem({
  item,
  active,
  onClick,
}: {
  item: FleetItem;
  active: boolean;
  onClick: () => void;
}) {
  const { device, position, status } = item;
  const nickname = getDeviceNickname(device);
  const telemetry = readTelemetry(position);
  const blocked = isBlocked(device, position);
  const speedNos = (position.speed ?? 0).toFixed(1).replace('.', ',');
  const hasTelemetry =
    telemetry.ignition !== undefined ||
    telemetry.batteryLevel !== undefined ||
    telemetry.battery !== undefined ||
    telemetry.blocked ||
    telemetry.alarm ||
    telemetry.charging;

  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'flex w-full items-start gap-3 p-3 text-left transition-colors',
          active ? 'bg-primary/10' : 'hover:bg-accent',
        )}
      >
        <DevicePhoto device={device} size="md" />
        <div className="min-w-0 flex-1 space-y-0.5">
          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-sm font-bold">{device.name}</span>
            <span
              className="h-2 w-2 shrink-0 rounded-full"
              style={{ background: STATUS_HEX[status] }}
              title={status}
            />
          </div>
          {nickname && (
            <p className="truncate text-xs font-medium text-foreground/70">{nickname}</p>
          )}
          {device.model && (
            <p className="truncate text-xs text-muted-foreground">{device.model}</p>
          )}
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 pt-0.5 text-[11px]">
            <span
              className="rounded px-1.5 font-semibold"
              style={{ background: `${STATUS_HEX[status]}1A`, color: STATUS_HEX[status] }}
            >
              {speedNos} nós
            </span>
            {blocked && (
              <span className="rounded bg-status-blocked/10 px-1.5 font-semibold text-status-blocked">
                Bloqueada
              </span>
            )}
            {deviceModo(device) && (
              <span className="rounded bg-brand/10 px-1.5 font-semibold text-brand">
                {deviceModo(device)}
              </span>
            )}
            <span className="text-muted-foreground">{formatRelativeTime(position.fixTime)}</span>
          </div>
          {position.address && (
            <p className="line-clamp-1 text-[11px] text-foreground/70">{formatAddress(position.address)}</p>
          )}
          {hasTelemetry && (
            <div className="pt-1">
              <TelemetryIcons telemetry={telemetry} />
            </div>
          )}
        </div>
      </button>
    </li>
  );
}

function PopupContent({
  device,
  position,
  onOpenCommands,
}: FleetItem & { onOpenCommands: () => void }) {
  const nickname = getDeviceNickname(device);
  const blocked = isBlocked(device, position);
  const speedKnots = position.speed ?? 0;
  const lat = position.latitude!;
  const lon = position.longitude!;

  const mapsUrl = `https://www.google.com/maps?q=${lat},${lon}`;
  const streetViewUrl = `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lon}`;

  return (
    <div className="space-y-3 p-1 text-sm">
      <div className="flex items-start gap-3">
        <DevicePhoto device={device} size="md" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-base font-bold">{device.name}</p>
          {nickname && <p className="truncate text-xs font-medium text-foreground/70">{nickname}</p>}
          {device.model && (
            <p className="truncate text-xs text-muted-foreground">{device.model}</p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {/* Dois selos independentes, lado a lado: o operacional e o de bloqueio. Um nunca
            esconde o outro. */}
        <StatusBadge status={computeOperationalStatus(device, position)} />
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold',
            blocked
              ? 'bg-status-blocked/10 text-status-blocked'
              : 'bg-status-running/10 text-status-running',
          )}
        >
          {blocked ? 'Bloqueada' : 'Desbloqueada'}
        </span>
        {deviceModo(device) && (
          <span className="inline-flex items-center gap-1 rounded-full bg-brand/10 px-2 py-0.5 text-[10px] font-semibold text-brand">
            Modo: {deviceModo(device)}
          </span>
        )}
      </div>

      <dl className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
        <div>
          <dt className="text-muted-foreground">Velocidade</dt>
          <dd className="font-semibold">{formatSpeed(speedKnots)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Rumo</dt>
          <dd className="font-semibold">
            {position.course != null ? `${Math.round(position.course)}°` : '—'}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Atualizado</dt>
          <dd className="font-semibold">{formatRelativeTime(position.fixTime)}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Coordenadas</dt>
          <dd className="font-mono text-[11px]">{formatCoords(lat, lon)}</dd>
        </div>
      </dl>

      {position.address && (
        <p className="flex items-start gap-1.5 text-xs text-foreground/80">
          <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
          <span>{formatAddress(position.address)}</span>
        </p>
      )}

      <p className="text-[10px] text-muted-foreground">
        Hora exata: {formatDateTime(position.fixTime)}
      </p>

      <div className="grid grid-cols-4 gap-1.5 pt-1">
        <PopupAction icon={Info} label="Detalhe" to={`/devices/${device.id}`} />
        <PopupAction
          icon={MapExternal}
          label="Maps"
          href={mapsUrl}
          color="text-status-ready"
        />
        <PopupAction icon={Eye} label="Ver" href={streetViewUrl} color="text-primary" />
        <PopupAction
          icon={Terminal}
          label="Comandos"
          onClick={onOpenCommands}
          color="text-status-blocked"
        />
      </div>
    </div>
  );
}

function PopupAction({
  icon: Icon,
  label,
  to,
  href,
  onClick,
  color = 'text-primary',
}: {
  icon: typeof Info;
  label: string;
  to?: string;
  href?: string;
  onClick?: () => void;
  color?: string;
}) {
  const className = cn(
    'flex flex-col items-center justify-center gap-1 rounded-lg bg-primary/5 px-1 py-2 text-[10px] font-semibold transition-colors hover:bg-primary/15',
    color,
  );
  const inner = (
    <>
      <Icon className="h-4 w-4" />
      {label}
    </>
  );
  if (to) return <Link to={to} className={className}>{inner}</Link>;
  if (href)
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={className}>
        {inner}
      </a>
    );
  return (
    <button type="button" onClick={onClick} className={className}>
      {inner}
    </button>
  );
}

interface CommandSpec {
  key: keyof typeof GT06_COMMANDS;
  label: string;
  icon: typeof Lock;
  color: string;
  bg: string;
  /** Exige confirmação dupla (ação crítica). */
  destructive?: boolean;
  /** Só permitido em baixa velocidade (segurança náutica). */
  requiresLowSpeed?: boolean;
}

/** Acima desta velocidade (nós) bloqueio/desbloqueio do motor ficam travados. */
const LOW_SPEED_MAX_KN = 3;

const COMMAND_SPECS: CommandSpec[] = [
  {
    key: 'block',
    label: 'Bloquear motor',
    icon: Lock,
    color: 'text-status-blocked',
    bg: 'bg-status-blocked/10',
    destructive: true,
    requiresLowSpeed: true,
  },
  {
    key: 'unblock',
    label: 'Desbloquear',
    icon: Unlock,
    color: 'text-status-running',
    bg: 'bg-status-running/10',
    requiresLowSpeed: true,
  },
  {
    key: 'reset',
    label: 'Reset',
    icon: RotateCcw,
    color: 'text-status-ready',
    bg: 'bg-status-ready/10',
  },
  {
    key: 'position',
    label: 'Forçar Posição',
    icon: Navigation,
    color: 'text-primary',
    bg: 'bg-primary/10',
  },
];

function CommandModal({ device, onClose }: { device: Device; onClose: () => void }) {
  const [pendingKey, setPendingKey] = useState<string | null>(null);
  const [confirmingKey, setConfirmingKey] = useState<CommandSpec['key'] | null>(null);
  const [customCmd, setCustomCmd] = useState('');
  const currentSpeedKn = useFleetStore((s) => s.positions.get(device.id)?.speed ?? 0);
  const [feedback, setFeedback] = useState<
    | { kind: 'success'; label: string; data: string; uid: string }
    | { kind: 'error'; label: string; message: string }
    | null
  >(null);
  const pushCmd = useCommandsStore((s) => s.push);
  const failCmd = useCommandsStore((s) => s.fail);
  const inferConfirmFromPosition = useCommandsStore((s) => s.inferConfirmFromPosition);
  const allRecords = useCommandsStore((s) => s.records);
  const deviceCommands = useMemo(
    () => allRecords.filter((r) => r.deviceId === device.id),
    [allRecords, device.id],
  );

  const mutation = useMutation({
    mutationFn: ({ data }: { key: string; label: string; data: string }) =>
      commandsApi.send(device.id, data),
    onSuccess: (_d, vars) => {
      const uid = pushCmd({
        deviceId: device.id,
        label: vars.label,
        data: vars.data,
        sentAt: Date.now(),
      });
      setFeedback({ kind: 'success', label: vars.label, data: vars.data, uid });
      setPendingKey(null);
      // Se o estado atual já bate com o comando enviado (mesma fonte que o
      // popup do mapa usa pra mostrar Bloqueada/Desbloqueada), marca como
      // executado direto, sem esperar nova posição.
      const currentPosition = useFleetStore.getState().positions.get(device.id);
      if (currentPosition) {
        const attrs = currentPosition.attributes ?? {};
        const blockedRaw = attrs.blocked;
        const blocked =
          typeof blockedRaw === 'boolean'
            ? blockedRaw
            : typeof blockedRaw === 'string'
              ? blockedRaw.toLowerCase() === 'true' || blockedRaw === '1'
              : typeof blockedRaw === 'number'
                ? blockedRaw !== 0
                : undefined;
        if (currentPosition.fixTime) {
          inferConfirmFromPosition(device.id, {
            blocked,
            fixTime: currentPosition.fixTime,
          });
        }
      }
    },
    onError: (err, vars) => {
      const msg = mensagemDeErro(err);
      setFeedback({ kind: 'error', label: vars.label, message: msg });
      setPendingKey(null);
    },
  });

  const dispatch = (spec: CommandSpec) => {
    setConfirmingKey(null);
    setFeedback(null);
    setPendingKey(spec.key);
    mutation.mutate({ key: spec.key, label: spec.label, data: GT06_COMMANDS[spec.key] });
  };

  const sendCustom = () => {
    const data = customCmd.trim();
    if (!data) return;
    setFeedback(null);
    setPendingKey('custom');
    mutation.mutate({ key: 'custom', label: `Comando: ${data}`, data });
  };

  const requestCommand = (spec: CommandSpec) => {
    setFeedback(null);
    // Trava de segurança náutica: bloqueio/desbloqueio só em baixa velocidade.
    if (spec.requiresLowSpeed && currentSpeedKn > LOW_SPEED_MAX_KN) {
      setConfirmingKey(null);
      setFeedback({
        kind: 'error',
        label: spec.label,
        message: `Disponível só em baixa velocidade (≤ ${LOW_SPEED_MAX_KN} nós). Atual: ${currentSpeedKn.toFixed(1).replace('.', ',')} nós.`,
      });
      return;
    }
    // Confirmação dupla para ações críticas.
    if (spec.destructive && confirmingKey !== spec.key) {
      setConfirmingKey(spec.key);
      return;
    }
    dispatch(spec);
  };
  void failCmd;

  const confirmingSpec = COMMAND_SPECS.find((s) => s.key === confirmingKey) ?? null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/60 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-sm rounded-xl bg-card p-5 shadow-xl ring-1 ring-border"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="text-base font-bold">Comandos</h3>
            <p className="truncate text-xs text-muted-foreground">{device.name}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-muted-foreground hover:bg-accent"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="mb-3 flex items-center justify-between gap-2 rounded-md bg-secondary/60 px-3 py-2 text-[11px]">
          <span className="text-muted-foreground">Velocidade atual</span>
          <span
            className={cn(
              'font-bold',
              currentSpeedKn > LOW_SPEED_MAX_KN ? 'text-status-ready' : 'text-status-running',
            )}
          >
            {currentSpeedKn.toFixed(1).replace('.', ',')} nós
            {currentSpeedKn > LOW_SPEED_MAX_KN ? ' · em movimento' : ' · seguro'}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-2">
          {COMMAND_SPECS.map((spec) => {
            const locked = !!spec.requiresLowSpeed && currentSpeedKn > LOW_SPEED_MAX_KN;
            return (
              <CommandButton
                key={spec.key}
                spec={spec}
                loading={pendingKey === spec.key}
                disabled={mutation.isPending}
                locked={locked}
                onClick={() => requestCommand(spec)}
              />
            );
          })}
        </div>

        {/* Comando personalizado: sirene, intervalo de transmissão, modos — a string varia por modelo de rastreador. */}
        <div className="mt-3 rounded-lg border border-border bg-secondary/40 p-2.5">
          <label className="text-[11px] font-semibold text-muted-foreground">Comando personalizado</label>
          <div className="mt-1 flex gap-2">
            <input
              value={customCmd}
              onChange={(e) => setCustomCmd(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') sendCustom(); }}
              placeholder="Ex.: SIREN,1#  ·  TIMER,30#  ·  MODE,..."
              className="h-9 flex-1 rounded-md border border-input bg-background px-2 text-xs"
            />
            <button
              type="button"
              onClick={sendCustom}
              disabled={mutation.isPending || !customCmd.trim()}
              className="rounded-md bg-primary px-3 text-xs font-bold text-primary-foreground disabled:opacity-50"
            >
              {pendingKey === 'custom' ? 'Enviando…' : 'Enviar'}
            </button>
          </div>
          <p className="mt-1 text-[10px] text-muted-foreground">
            Sirene, intervalo de transmissão e modos dependem do modelo do rastreador — digite a string exata do equipamento.
          </p>
        </div>

        {confirmingSpec && (
          <div className="mt-4 rounded-lg border border-status-blocked/30 bg-status-blocked/10 p-3">
            <p className="flex items-start gap-2 text-xs font-semibold text-status-blocked">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Confirmar "{confirmingSpec.label}"? Esta ação afeta o motor da embarcação.
            </p>
            <div className="mt-3 flex gap-2">
              <button
                type="button"
                onClick={() => dispatch(confirmingSpec)}
                disabled={mutation.isPending}
                className="flex-1 rounded-lg bg-status-blocked px-3 py-2 text-xs font-bold text-white hover:opacity-90 disabled:opacity-50"
              >
                Sim, {confirmingSpec.label.toLowerCase()}
              </button>
              <button
                type="button"
                onClick={() => setConfirmingKey(null)}
                className="flex-1 rounded-lg bg-secondary px-3 py-2 text-xs font-semibold hover:bg-accent"
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        {feedback && (
          <div
            className={cn(
              'mt-4 flex items-start gap-2 rounded-md px-3 py-2 text-xs font-medium',
              feedback.kind === 'success'
                ? 'bg-status-running/10 text-status-running'
                : 'bg-status-blocked/10 text-status-blocked',
            )}
          >
            {feedback.kind === 'success' ? (
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            ) : (
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            )}
            <div className="space-y-0.5">
              <p className="font-semibold">
                {feedback.kind === 'success'
                  ? `Comando "${feedback.label}" enviado ao servidor`
                  : `Não foi possível enviar "${feedback.label}"`}
              </p>
              {/* Monoespaçado só na resposta crua do equipamento; explicação de erro é texto
                  corrido, para ser lida como frase e não como log. */}
              <p
                className={
                  feedback.kind === 'success'
                    ? 'font-mono text-[10px] opacity-80'
                    : 'text-[11px] leading-snug opacity-90'
                }
              >
                {feedback.kind === 'success' ? feedback.data : feedback.message}
              </p>
              {feedback.kind === 'success' && (
                <p className="text-[10px] opacity-80">
                  Aguardando confirmação da embarcação abaixo.
                </p>
              )}
            </div>
          </div>
        )}

        {deviceCommands.length > 0 && (
          <div className="mt-4">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Histórico recente
            </p>
            <ul className="max-h-48 divide-y divide-border overflow-y-auto rounded-md border border-border">
              {deviceCommands.slice(0, 10).map((r) => (
                <CommandHistoryRow key={r.uid} record={r} />
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function CommandHistoryRow({ record }: { record: import('@/stores/commands').CommandRecord }) {
  const sentRelative = formatRelativeTime(new Date(record.sentAt).toISOString());
  // 'timeout' nunca é marcado mais (o Traccar nem sempre devolve commandResult),
  // mas pode existir em histórico antigo persistido. Tratamos como 'sent'.
  const effectiveStatus = record.status === 'timeout' ? 'sent' : record.status;
  // RESET# é fire-and-forget: o equipamento não devolve confirmação por
  // protocolo. Tratamos como status terminal "Comando enviado" logo após
  // o servidor aceitar, sem spinner pendurado.
  const isFireAndForget = record.data === 'RESET#';

  let statusLabel = 'Enviado';
  let statusColor = 'text-status-ready';
  let StatusIcon: typeof Check = Loader2;
  let showSpinner = effectiveStatus === 'sent';
  if (effectiveStatus === 'confirmed') {
    statusLabel = 'Executado pela embarcação';
    statusColor = 'text-status-running';
    StatusIcon = Check;
  } else if (effectiveStatus === 'failed') {
    statusLabel = `Falhou${record.result ? `: ${record.result}` : ''}`;
    statusColor = 'text-status-blocked';
    StatusIcon = AlertCircle;
  } else if (effectiveStatus === 'sent' && isFireAndForget) {
    statusLabel = 'Comando enviado';
    statusColor = 'text-status-running';
    StatusIcon = Check;
    showSpinner = false;
  }
  return (
    <li className="flex items-start justify-between gap-2 px-3 py-2 text-[11px]">
      <div className="min-w-0">
        <p className="font-semibold">{record.label}</p>
        <p className="font-mono text-[10px] text-muted-foreground">{record.data}</p>
        <p className="text-muted-foreground">{sentRelative}</p>
      </div>
      <span className={cn('inline-flex items-center gap-1 font-semibold', statusColor)}>
        <StatusIcon className={cn('h-3 w-3', showSpinner && 'animate-spin')} />
        {statusLabel}
      </span>
    </li>
  );
}

function CommandButton({
  spec,
  loading,
  disabled,
  locked,
  onClick,
}: {
  spec: CommandSpec;
  loading: boolean;
  disabled: boolean;
  locked?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={locked ? `Disponível só em baixa velocidade (≤ ${LOW_SPEED_MAX_KN} nós)` : undefined}
      className={cn(
        'relative flex flex-col items-center gap-1.5 rounded-lg px-3 py-3 text-xs font-semibold transition-colors',
        spec.bg,
        spec.color,
        disabled ? 'opacity-50' : 'hover:opacity-80',
        locked && 'opacity-60',
      )}
    >
      {locked && (
        <Lock className="absolute right-1.5 top-1.5 h-3 w-3 text-status-ready" />
      )}
      {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <spec.icon className="h-5 w-5" />}
      {spec.label}
    </button>
  );
}
